/**
 * @fileoverview Writing a page: build the event, REFUSE IT LOCALLY IF IT IS TOO
 * BIG, sign it with the author's own signer, and send it.
 *
 * THE SIZE PRE-CHECK IS THE REASON THIS FILE IS SHAPED THE WAY IT IS. A relay
 * that receives an oversized websocket message does not answer `OK false`; it
 * CLOSES THE CONNECTION, with no OK and no NOTICE. Every client library
 * surfaces that as a generic socket failure, indistinguishable from a network
 * drop. An author whose page crossed the ceiling would therefore see "something
 * went wrong", see it on every retry, and have no reason to suspect the page
 * rather than their connection. They would lose the work.
 *
 * So the measurement happens here, before anything is sent, against the limit
 * the TARGET relay advertises, and an over-limit page is refused with a message
 * that names the page and the number. Nothing about this is a nicety.
 *
 * IT IS MEASURED BEFORE THE SIGNER IS ASKED AND BEFORE THE UPLOAD, not after
 * either. Signing a page we already know we will throw away costs the author a
 * prompt -- and on a remote signer, a notification on their phone -- for a
 * decision we had already made; uploading its body first costs them a second
 * prompt, because a Blossom upload is authorised by an event their own signer
 * produces, and puts bytes on somebody else's server for a page that will never
 * exist. The size is knowable without a signature, because an event's id and sig
 * are fixed-length hex, and knowable before the upload because a blob's hash is
 * fixed-length too: see `measureEventFrame` and `FLOOR_BLOB`.
 *
 * FAILURES ARE NOT COLLAPSED HERE, WHICH IS DELIBERATE AND IS THE OPPOSITE OF
 * THE READ PATH. `loadPage` flattens every outcome into one "not available",
 * because a stranger must not be able to distinguish a private page from a
 * missing one. An author publishing their own page is the one person entitled
 * to know exactly what happened, and the vagueness that protects a reader's
 * privacy would here just destroy their afternoon.
 *
 * EVERY PAGE THIS FILE WRITES IS A 33301, whatever its format and whoever may
 * read it. Operator ruling 2026-09-16, REVERSING the 2026-09-15 ruling that a
 * public markdown page is an ordinary NIP-23 long-form event with its body
 * inline.
 *
 *   markdown -> 33301, body uploaded to Blossom, `x` tag carrying its sha256.
 *   html     -> 33301, the same, and shown in a sandboxed frame rather than
 *               through the markdown renderer.
 *   private  -> 33301, body encrypted before it is uploaded and every tag but
 *               the name sealed. See `privatePage.ts`.
 *
 * WHY IT REVERSED, because the earlier reasoning was sound and the thing that
 * changed is the PRODUCT. 30023 was worth shaping the event around while a page
 * was prose: six of the seven long-form clients we measured rendered our pages
 * from the address alone, with no work from us. A page is no longer prose. Pages
 * carries a structural vocabulary and a theme system, so publishing one as
 * long-form means publishing a DEGRADED COPY under the author's own name --
 * directives rendering as literal text, the theme ignored entirely. Full spec:
 * `arbiter/cloistr/architecture/page-format.md`.
 *
 * WHAT IT DELETES, AND THAT PART IS NOT A TRADE. The cost the operator accepted
 * -- a visibility flip changing a markdown page's kind, therefore its address,
 * therefore every `naddr` anyone holds -- EXISTED ONLY BECAUSE public markdown
 * was 30023 while private was ours. Under one kind the flip changes a property
 * of the event and nothing about where it lives, so links people already hold
 * keep working, and the markdown flip stops being something this app has to
 * decline.
 *
 * WHAT IT COSTS, said plainly here because user-facing copy must not overclaim
 * in either direction. None of those six clients will render our kind. "Readable
 * in any Nostr client" was true of a 30023 and is now false of us, and the
 * honest replacement is that pages are PORTABLE -- open format, exportable,
 * hostable anywhere -- rather than universally renderable. Reading somebody
 * else's 30023 stays, because that is a capability of the reader and it costs
 * nothing. Republishing our own pages as a second 30023 is still dead, for the
 * reason it was always dead: two events holding one text, free to drift.
 *
 * 33301 MEANS "THE BODY IS A BLOB", and now every page's body is one.
 * Encryption is a property the EVENT carries, not the kind, and the
 * discriminator is the presence of a plaintext `x` tag. A private page reaches
 * the people it was shared with by a per-page random key, NIP-44 encrypted to
 * each recipient, carried on a kind 30080 share event (Stash's, reused rather
 * than a second one of ours) and found by the recipient with a relay query on
 * their own `p` tag, with no server of ours in the path.
 *
 * THE SIZE PRE-CHECK SURVIVES AND STOPS BEING LOAD-BEARING. With the body out of
 * the event, no realistic page approaches the relay's ceiling, so the check is
 * now cheap insurance on a small index event rather than the only thing standing
 * between an author and a permanently unexplained failure. It stays for the
 * reason at the top of this file, which did not change: what it catches is a
 * closed socket that reads as "something went wrong", forever.
 *
 * EDITING REPUBLISHES TO THE SAME `d`. The event is addressable, so the
 * coordinate is the page's durable name and the `naddr` survives every edit.
 * That is the entire reason the URL holds an address and not an event id, and
 * it is why `identifier` is an input to this function rather than something it
 * generates.
 *
 * ONE AUTHOR, ONE PAGE PER NAME, AND THE CHECK STILL LOOKS IN BOTH KINDS THOUGH
 * WE NOW WRITE ONLY ONE. The readable URL form `<author>/<slug>` is the one we
 * tell people to share, and `resolveReadable` answers it by looking for that
 * name under 30023 as well as ours -- because an author's long-form articles,
 * written in somebody else's client, live in the same readable namespace. Two
 * events at one name make that form name neither of them. What the reversal
 * removed is the case where OUR OWN publisher created the collision by changing
 * a page's kind; what remains is a real case we do not control, so the query is
 * unchanged. See `checkName`, which refuses when a relay did not answer.
 *
 * AND BECAUSE IT DOES, THE TIMESTAMP IS LOAD-BEARING. Two edits in one second
 * carry the same `created_at`, the relay accepts both and keeps the one with the
 * lower event id, and the author's newer text is discarded after being reported
 * as published. Measured, not feared -- see `nextCreatedAt`, which is what stops
 * it.
 */

import type { ContentKey, SignerInterface } from '@cloistr/auth/core';
import type { Event, EventTemplate, UnsignedEvent } from 'nostr-tools';

import { ownerKeyTag } from './collectionKeys';
import { type PageFormat, type PageLayout } from './indexEvent';
import { NIP23_LONG_FORM_KIND, PAGE_INDEX_KIND } from './kinds';
import { checkName } from './nameCheck';
import { encryptWithPageKey, generatePageKey } from './pageKey';
import { type SealedPage, sealPageTags } from './privatePage';
import { isValidIdentifier } from './slug';

import { config } from '@/config/environment';
import { uploadBlob } from '@/services/blossom/uploadBlob';
import { encodeAddress } from '@/services/nostr/pageLinks';
import { fetchRelayLimits } from '@/services/nostr/relayInfo';
import { publishEvent, relaysFor } from '@/services/nostr/relays';


export interface PageDraft {
  /** The `d` tag: the page's durable name. Same value on every edit. */
  identifier: string;
  title: string;
  summary: string;
  format: PageFormat;
  /** Markdown source, or raw HTML. */
  body: string;
  /** Addressable coordinate of a theme event, or null. */
  themeAddress?: string | null;
  layout?: Partial<PageLayout>;
  /**
   * Original publication time, preserved across edits.
   *
   * An edit that resets this makes a two-year-old page look new in every
   * reader that sorts by it, so the editor passes back what it loaded.
   */
  publishedAt?: number | null;
  /**
   * The page this publish REPLACES, when an existing page is being edited.
   *
   * Absent means a new page, and a new page may not take a name the author has
   * already used under either kind. Present, it excuses the one event that is
   * allowed to be there: the previous version of this very page, at this very
   * coordinate. It does NOT excuse an event at the same name under the OTHER
   * kind -- a long-form article this author published in some other client --
   * because that one is not the page being replaced and still leaves the
   * readable URL naming two things.
   */
  replaces?: PageAddress | null;
  /**
   * True for a PRIVATE page: the body is encrypted before it is uploaded and
   * every tag but the name is sealed.
   *
   * IT NO LONGER CHANGES THE KIND, SO IT NO LONGER CHANGES THE ADDRESS. Every
   * page is a 33301 since 2026-09-16, so a flip in either direction republishes
   * at the same coordinate and every link anyone holds keeps working. That was
   * not true while a public markdown page was a long-form event and a private
   * one was ours, and the cost the operator accepted for that flip is GONE
   * rather than mitigated.
   *
   * What the flip still cannot undo is said by `VisibilityChoice` in
   * `PageEditor.tsx`, at the moment of flipping: going private does not retract
   * bytes that were already served openly, and the page's NAME is public whether
   * or not the page is.
   */
  private?: boolean;
  /**
   * The page's key.
   *
   * REQUIRED WHEN EDITING an existing private page, and omitted only when one is
   * being created. Publishing an edit under a freshly minted key would re-seal
   * the page against a key nobody has been given: every recipient is revoked,
   * nothing says so, and the author's own screen looks right because their owner
   * envelope was rewritten in the same act. Same failure as a collection
   * publisher minting a key on every write, and refused the same way.
   */
  pageKey?: ContentKey | null;
}

export interface PageAddress {
  kind: number;
  pubkey: string;
  identifier: string;
}

export type PublishOutcome =
  | {
      status: 'published';
      address: PageAddress;
      naddr: string;
      acceptedBy: string[];
      /**
       * The key this page is sealed under. PRIVATE PAGES ONLY.
       *
       * Handed back rather than left to be recovered from the owner envelope,
       * because the two things an author does immediately after creating a
       * private page -- share it, and edit it again -- both need it, and both
       * would otherwise cost a relay round trip and a signer prompt to get back
       * a value this function was holding a moment ago.
       */
      pageKey?: ContentKey;
    }
  /** Refused locally, before sending. The author keeps their work. */
  | {
      status: 'too-large';
      bytes: number;
      limit: number;
      /** False when the relay advertised no limit and a default was assumed. */
      advertised: boolean;
      relay: string;
    }
  /**
   * The author already has a page at this name. Refused before anything was
   * uploaded or signed, because two pages at one name break the readable URL.
   */
  | { status: 'slug-taken'; identifier: string; conflictsWith: PageAddress }
  /**
   * Nothing was found at this name, but these relays never answered, so it is
   * not known to be free. Refused: a relay failure is an error, not "free".
   */
  | { status: 'name-unchecked'; identifier: string; relays: string[] }
  /**
   * An edit of a private page arrived without the page's key.
   *
   * Refused rather than served with a fresh one: re-sealing under a new key
   * revokes every recipient silently, and the author's own screen still works.
   */
  | { status: 'key-required' }
  /** The body never reached a blob server, so the event would point at nothing. */
  | { status: 'upload-failed'; refused: string[] }
  /** Every relay refused it, in their own words. */
  | { status: 'rejected'; refused: { relay: string; reason: string }[] }
  /** Bad draft, or the signer said no. */
  | { status: 'failed'; reason: string };

const DEFAULT_LAYOUT: PageLayout = { toc: false, width: 'prose', columns: 1 };

/**
 * A stand-in for the blob, so the size can be measured BEFORE the upload.
 *
 * THIS EXISTS BECAUSE OF THE 2026-09-16 RULING AND NOT BEFORE IT. While public
 * markdown travelled inside its event, the only page that needed an upload was
 * one whose size the event did not depend on, so a single exact measurement
 * after the upload was both early enough and exact. Now every page is uploaded
 * first, and a Blossom upload is authorised by a kind 24242 event THE AUTHOR'S
 * SIGNER PRODUCES -- so without this, an over-limit page would cost a signing
 * prompt and a round trip to somebody else's file server before being refused
 * locally, which is precisely what the top of this file promises it will not do.
 *
 * The hash is fixed-length lowercase hex and measures exactly what a real one
 * will. The SERVER LIST IS LEFT EMPTY, which makes the whole measurement a
 * strict LOWER BOUND on the event that eventually goes out -- and undercounting
 * is the only safe direction. A page refused on this measurement is one whose
 * inline fields alone cross the ceiling; nothing that would have fitted is ever
 * turned away by it. The exact measurement still runs afterwards, on the real
 * event, before the signature.
 */
const FLOOR_BLOB: { hash: string; servers: string[] } = { hash: '0'.repeat(64), servers: [] };

/**
 * The last `created_at` this process used for each address.
 *
 * ================================================================
 * THIS IS NOT A PAGES PROBLEM, AND IT IS NOT ONLY AN ADDRESSABLE
 * ONE. Plain REPLACEABLE events carry the identical tie-break, so
 * a profile and a follow list are in scope alongside a page, a
 * document and a theme. Anything that republishes over its own
 * previous version has the hole, and its mocked tests will pass.
 * If you are reading this from another repository, the whole
 * reasoning is below, on purpose.
 * ================================================================
 *
 * MEASURED AGAINST relay.cloistr.xyz 2026-09-15, AND IT IS A FALSE SUCCESS.
 * Two edits of one page inside the same second produce two addressable events
 * with an identical `created_at`. The relay answered `OK true` TO BOTH, and then
 * kept the one with the LOWER EVENT ID -- NIP-01's tie-break for equal
 * timestamps. So the author's second write, the newer one, was acknowledged as
 * published and then discarded. Verified by publishing "FIRST" and "SECOND"
 * within one second and reading back "FIRST".
 *
 * IT IS WORSE THAN THE SIZE CEILING, AND THE DIFFERENCE IS THE POINT. The two
 * have the same shape and not the same consequence. Over the size limit the
 * socket closes, nothing lands, and the author eventually works out that
 * something is wrong. Here the relay POSITIVELY ACKNOWLEDGES an event it is
 * about to throw away. That is not a silent failure, it is a false success, and
 * no amount of user patience surfaces it: every signal the author can see says
 * the edit was saved.
 *
 * A one-second window sounds too narrow to matter until you look at who
 * publishes. A save button that is pressed twice, a retry after a signer
 * hiccup, or the directory-of-markdown publisher this app is meant to grow --
 * anything that republishes one page programmatically lives entirely inside
 * that window.
 */
const lastCreatedAt = new Map<string, number>();

/**
 * A timestamp that is strictly greater than the last one used for this address.
 *
 * Normally just the wall clock. It only diverges when two publishes of the SAME
 * page land in one second, which is exactly the case the relay would otherwise
 * resolve against the author.
 *
 * PER ADDRESS, NOT GLOBAL, AND THIS IS THE PART THAT DOES NOT SURVIVE BEING
 * SUMMARISED. A global "last timestamp used" would also fix the bug, so the
 * distinction looks like a detail and is not.
 *
 * A relay only ever compares two events when they share a kind, an author AND
 * an identifier. That triple is what an addressable event is addressed by, and
 * it is the only thing the tie-break can apply to. (For a plain replaceable
 * event, kind and author alone are the address, and the same argument holds
 * with the identifier dropped.) Two DIFFERENT pages published in the same
 * second are not in conflict at all, and never were.
 *
 * So a global counter would push the timestamp of every page forward to stay
 * ahead of whichever page was published last. Publish forty pages in a burst,
 * as a directory publisher does, and the fortieth is stamped thirty-nine
 * seconds after it was written. Those timestamps are what every reader sorts
 * by, so the cost is paid in the wrong place, by readers, to fix a collision
 * that only two versions of ONE page can have. Keyed by address, the bump
 * happens only where the conflict is real.
 *
 * The relay refuses events more than 300 seconds in the future, so this could
 * in principle run ahead of itself after 300 republishes of one page inside one
 * second. That is not a scenario, and if it ever happened the relay would
 * refuse the event OUT LOUD rather than swallow it -- which is the right way
 * round, and the whole point of this file.
 */
/**
 * EXPORTED for the collection publisher, which republishes over its own previous
 * version exactly as a page does and has the identical silent-discard hole.
 *
 * The map is deliberately SHARED rather than duplicated: the rule is per
 * address, a collection and a page never share an address, and two maps would
 * be two things to keep in step for no benefit.
 */
export function nextCreatedAt(addressKey: string): number {
  const now = Math.floor(Date.now() / 1000);
  const last = lastCreatedAt.get(addressKey);
  const next = last !== undefined && last >= now ? last + 1 : now;
  lastCreatedAt.set(addressKey, next);
  return next;
}

/**
 * The exact byte length of the websocket frame this event will become.
 *
 * EXACT, NOT ESTIMATED, and it matters that it is exact: the check has to be
 * safe to run before signing, and an estimate would either refuse pages that
 * would have fit or let through ones that will not.
 *
 * It works because the two fields a signature adds are fixed-length lowercase
 * hex -- 64 characters of id and 128 of sig, one byte each -- so a placeholder
 * of the right length serialises to exactly the same number of bytes as the
 * real thing. The pubkey is known before signing too.
 *
 * WHAT IS MEASURED IS THE WHOLE FRAME, `["EVENT",{…}]`, not the event object.
 * The relay's limit is applied by the websocket layer to the entire message, so
 * measuring the event alone would undercount by the wrapper and let a page
 * through that sits just under the ceiling.
 */
export function measureEventFrame(event: UnsignedEvent): number {
  const withSignature = { ...event, id: '0'.repeat(64), sig: '0'.repeat(128) };
  return new TextEncoder().encode(JSON.stringify(['EVENT', withSignature])).length;
}

/**
 * The media type a page's body actually is, before anything is done to it.
 *
 * NOT what a PRIVATE page's blob is uploaded as. Ciphertext goes up as
 * `text/plain`, deliberately -- see `publishPage`. This is the type RECORDED IN
 * THE EVENT, which for a private page sits inside the sealed tag and so tells a
 * stranger holding the address nothing.
 */
function mimeFor(format: PageFormat): string {
  return format === 'markdown' ? 'text/markdown' : 'text/html';
}

/**
 * Build the unsigned event for a PUBLIC page, of either format.
 *
 * ONE SHAPE FOR BOTH FORMATS since the ruling of 2026-09-16. This function used
 * to have two exits: markdown returned a long-form event with the body in
 * `.content`, and only HTML was blob-backed. Now the body is a blob whatever it
 * is written in, so `format` is a TAG rather than a branch, and the only thing
 * it still decides is which media type is recorded beside the hash.
 *
 * THE BLOB IS REQUIRED, and that is a signature saying something true. An event
 * published before its upload succeeded names bytes no server holds, and such a
 * page is unavailable forever while looking, to every reader, exactly like one
 * that was deleted. It used to be optional so the editor could measure an inline
 * draft as it was typed; there is no inline draft to measure any more.
 *
 * `createdAt` is passed in by `publishPage`, which keeps it strictly increasing
 * per address -- see `nextCreatedAt`, and the silent discard it exists to stop.
 */
export function buildPageEvent(
  draft: PageDraft,
  pubkey: string,
  blob: { hash: string; servers: string[] },
  createdAt: number = Math.floor(Date.now() / 1000)
): UnsignedEvent {
  const layout = { ...DEFAULT_LAYOUT, ...draft.layout };

  const tags: string[][] = [
    ['d', draft.identifier],
    ['title', draft.title],
  ];
  if (draft.summary) tags.push(['summary', draft.summary]);
  tags.push(['published_at', String(draft.publishedAt ?? createdAt)]);

  if (draft.themeAddress) tags.push(['theme', draft.themeAddress]);
  if (layout.toc) tags.push(['toc', 'true']);
  if (layout.width !== 'prose') tags.push(['width', layout.width]);
  if (layout.columns !== 1) tags.push(['columns', String(layout.columns)]);

  tags.push(
    ['x', blob.hash],
    ['format', draft.format],
    ['m', mimeFor(draft.format)],
    ['updated_at', String(createdAt)],
    ...blob.servers.map((server) => ['server', server])
  );

  return {
    kind: PAGE_INDEX_KIND,
    pubkey,
    created_at: createdAt,
    tags,
    // A PLAINTEXT ABSTRACT, never the pointer -- the pointer is the `x` tag.
    // Keeping content free for prose is what lets the relay's NIP-50 search
    // find a page at all, now that every body is bytes we never send.
    content: draft.summary,
  };
}

/**
 * Build the unsigned event for a PRIVATE page.
 *
 * Separate from `buildPageEvent` rather than a branch inside it, because the two
 * have almost nothing in common once you look: the public builder's whole body
 * is decisions about which tags to emit, and every one of those decisions is
 * answered here by "seal it". A shared function would be two functions sharing a
 * name.
 *
 * `ownerEnvelope` is passed in rather than made here because wrapping a key to
 * the owner needs the signer, and this function is deliberately synchronous so
 * the EDITOR CAN MEASURE A DRAFT AS IT IS TYPED without a signer prompt per
 * keystroke. An envelope always wraps exactly 32 bytes, so every envelope is the
 * same length and a placeholder measures to the same number of bytes as the real
 * one -- the same argument `measureEventFrame` makes about the id and the sig.
 */
export function buildPrivatePageEvent(
  draft: PageDraft,
  pubkey: string,
  blob: { hash: string; servers: string[] },
  pageKey: ContentKey,
  ownerEnvelope: readonly string[],
  createdAt: number = Math.floor(Date.now() / 1000)
): UnsignedEvent {
  const layout = { ...DEFAULT_LAYOUT, ...draft.layout };

  const sealed: SealedPage = {
    title: draft.title,
    summary: draft.summary,
    format: draft.format,
    x: blob.hash,
    m: mimeFor(draft.format),
    servers: blob.servers,
    published_at: draft.publishedAt ?? createdAt,
    updated_at: createdAt,
  };
  if (draft.themeAddress) sealed.theme = draft.themeAddress;
  if (layout.toc) sealed.toc = true;
  if (layout.width !== 'prose') sealed.width = layout.width;
  if (layout.columns !== 1) sealed.columns = layout.columns;

  const { tags, content } = sealPageTags(draft.identifier, sealed, pageKey, ownerEnvelope);

  return { kind: PAGE_INDEX_KIND, pubkey, created_at: createdAt, tags, content };
}

/**
 * Publish a page. One relay's acceptance is a success.
 *
 * The target relays are our configured ones; a page published to several is
 * more findable, and the address carries hints so a reader can follow it.
 */
export async function publishPage(
  draft: PageDraft,
  signer: SignerInterface,
  relayUrls: string[] = relaysFor([])
): Promise<PublishOutcome> {
  const identifier = draft.identifier.trim().toLowerCase();
  if (!isValidIdentifier(identifier)) {
    return {
      status: 'failed',
      reason:
        'A page needs a name made of lowercase letters, numbers and hyphens. ' +
        'It becomes part of the page’s permanent address, so it cannot be empty.',
    };
  }
  if (!draft.title.trim()) {
    return { status: 'failed', reason: 'A page needs a title.' };
  }
  if (relayUrls.length === 0) {
    return { status: 'failed', reason: 'There is no relay to publish to.' };
  }

  // THE KEY CHECK IS THE CHEAPEST REFUSAL OF ALL AND IT GOES ABOVE THE SIGNER.
  // It was one line lower and the comment claimed this ordering it did not
  // have: the signer's public key was fetched first, so the claim "before the
  // signer" was stronger than the code. Caught in review 2026-09-16. Nothing
  // was signed and nothing prompted either way, so no author was ever harmed by
  // it -- but a comment that overstates what the code does is how the next
  // person reasons about a path that is not there, so the code was moved to
  // match the claim rather than the claim weakened to match the code.
  //
  // An edit of a private page that arrived without its key would otherwise be
  // discovered after the author had waited for an upload and approved a
  // signature, and the thing it prevents is silent: a fresh key re-seals the
  // page against one nobody has been given.
  const replaces = draft.replaces ?? null;
  let pageKey: ContentKey | undefined;
  if (draft.private) {
    if (replaces !== null && !draft.pageKey) return { status: 'key-required' };
    pageKey = draft.pageKey ?? generatePageKey();
  }

  let pubkey: string;
  try {
    pubkey = await signer.getPublicKey();
  } catch (e) {
    return { status: 'failed', reason: signerMessage(e) };
  }

  // EVERY PAGE THIS APP WRITES IS A 33301 -- public or private, markdown or raw
  // HTML. Ruling of 2026-09-16; the top of this file has what it deletes. It is
  // named rather than inlined because the coordinate below is built from it
  // three times, and a reader tracing the address should see the same symbol
  // each time rather than a literal that happens to agree.
  const kind = PAGE_INDEX_KIND;

  // THE NAME CHECK COMES BEFORE THE UPLOAD AND BEFORE THE SIGNER, for the same
  // reason the size check does: a page we are going to refuse must not cost the
  // author a Blossom upload or a signing prompt on their phone first.
  //
  // An event already at this author's name is allowed in exactly one case --
  // it is the previous version of the page being edited, at the same
  // coordinate. `replaces` has to agree with what is actually being written on
  // every part of the address, so that a caller cannot wave the check away by
  // naming a page it is not in fact replacing.
  const replacing =
    replaces !== null &&
    replaces.kind === kind &&
    replaces.pubkey === pubkey &&
    replaces.identifier === identifier;

  const name = await checkName(
    [NIP23_LONG_FORM_KIND, kind],
    pubkey,
    identifier,
    relayUrls,
    replacing ? kind : undefined
  );
  if (name.status === 'taken') {
    return { status: 'slug-taken', identifier, conflictsWith: name.holder };
  }
  if (name.status === 'unchecked') {
    return { status: 'name-unchecked', identifier, relays: name.relays };
  }

  const coordinate = `${kind}:${pubkey}:${identifier}`;

  // THE OWNER'S ENVELOPE IS MADE BEFORE THE UPLOAD, not after, so that the
  // measurement below is taken on the event that will really be published rather
  // than on a guess at its shape. It rides on the PAGE's own event and never
  // anywhere else: a container cannot take down what it does not carry, and if
  // the only route to a page key were inside a collection, deleting that
  // collection would leave the page's ciphertext in Blossom openable by nobody,
  // its author included, permanently. See `collectionKeys.ts`.
  let envelope: readonly string[] | undefined;
  if (pageKey) {
    try {
      envelope = await ownerKeyTag(pageKey, coordinate, signer);
    } catch (e) {
      return { status: 'failed', reason: signerMessage(e) };
    }
  }

  const build = (blob: { hash: string; servers: string[] }, createdAt?: number): UnsignedEvent =>
    pageKey
      ? buildPrivatePageEvent(
          { ...draft, identifier },
          pubkey,
          blob,
          pageKey,
          envelope as readonly string[],
          createdAt
        )
      : buildPageEvent({ ...draft, identifier }, pubkey, blob, createdAt);

  const target = relayUrls[0] as string;
  const limits = await fetchRelayLimits(target);

  // THE SIZE REFUSAL, AND IT IS HERE RATHER THAN LOWER DOWN FOR A REASON THAT
  // ONLY EXISTS SINCE EVERY BODY BECAME A BLOB. Below this line the author pays
  // for a signature and an upload to somebody else's file server; a page we have
  // already decided to throw away must not cost them either. See `FLOOR_BLOB`
  // for why measuring without the real hash is sound, and why it can only ever
  // refuse a page that was certainly going to be refused.
  const floor = measureEventFrame(build(FLOOR_BLOB));
  if (floor > limits.maxMessageBytes) {
    return {
      status: 'too-large',
      bytes: floor,
      limit: limits.maxMessageBytes,
      advertised: limits.advertised,
      relay: target,
    };
  }

  // The blob goes up FIRST, because its hash is part of the event. An event
  // published before a successful upload would name bytes no server holds, and
  // that page is unavailable forever while looking, to every reader, exactly
  // like one that was deleted.
  //
  // EVERY PAGE TAKES THIS PATH NOW. Markdown used to skip it and travel inside
  // the event, which is the whole of what the 2026-09-16 ruling changed here.
  //
  // WHAT IS UPLOADED FOR A PRIVATE PAGE IS THE CIPHERTEXT, and its hash is
  // therefore the ciphertext's. That ordering is what lets a reader check the
  // bytes a server handed it against something the author signed BEFORE those
  // bytes reach the crypto.
  const body = pageKey ? encryptWithPageKey(draft.body, pageKey) : draft.body;
  // For a private page `text/plain`, not the body's own type, and this is the
  // smallest leak in the design rather than a formatting detail: a blob server
  // told `text/html` has been told what kind of page it is holding. NIP-44
  // output is base64 text, so the honest type for the bytes actually being
  // stored is the one that says nothing about the page inside them. A public
  // page has nothing to withhold and is uploaded as what it is.
  const mimeType = pageKey ? 'text/plain' : mimeFor(draft.format);
  const bodyBytes = new TextEncoder().encode(body);
  let outcome;
  try {
    outcome = await uploadBlob(
      bodyBytes.buffer,
      mimeType,
      config.fallbackBlossomServers,
      signer
    );
  } catch (e) {
    return { status: 'failed', reason: signerMessage(e) };
  }
  if (outcome.servers.length === 0) {
    return { status: 'upload-failed', refused: outcome.refused };
  }
  const blob = { hash: outcome.hash, servers: outcome.servers };

  // Reserved per ADDRESS, and as late as possible: the timestamp has to be the
  // one this event is actually sent with, not one that sat unused while a blob
  // was uploaded.
  const createdAt = nextCreatedAt(coordinate);
  const unsigned = build(blob, createdAt);

  // AND THE EXACT ONE, STILL BEFORE THE SIGNATURE. What it adds over the floor
  // above is the `server` tags, which are the only part of the event whose
  // length was unknown until the upload answered. A page that gets this far and
  // fails here has left an unreferenced blob on a file server; that is the price
  // of the servers being unknowable in advance, and it is paid by a page that
  // was within a few hundred bytes of a half-megabyte ceiling.
  const bytes = measureEventFrame(unsigned);
  if (bytes > limits.maxMessageBytes) {
    return {
      status: 'too-large',
      bytes,
      limit: limits.maxMessageBytes,
      advertised: limits.advertised,
      relay: target,
    };
  }

  let signed: Event;
  try {
    signed = await signer.signEvent(unsigned);
  } catch (e) {
    return { status: 'failed', reason: signerMessage(e) };
  }

  const { accepted, refused } = await publishEvent(relayUrls, signed, (template: EventTemplate) =>
    signer.signEvent({ ...template, pubkey })
  );

  if (accepted.length === 0) {
    return { status: 'rejected', refused };
  }

  const address: PageAddress = { kind: unsigned.kind, pubkey, identifier };
  return {
    status: 'published',
    address,
    naddr: encodeAddress({ ...address, relays: accepted }),
    acceptedBy: accepted,
    ...(pageKey ? { pageKey } : {}),
  };
}

/**
 * A signer's own words where there are any.
 *
 * A remote signer's refusal ("user rejected") and an extension that is not
 * installed are different situations for the author, and flattening them into
 * "signing failed" would hide the one they can act on.
 */
function signerMessage(e: unknown): string {
  if (e instanceof Error && e.message) return e.message;
  return 'The signer did not complete the request.';
}
